import type { Session, User } from 'better-auth';

import type { GetProfileResponse } from '~~/server/api/profile/index.get';

import type { SessionInfo } from '~~/server/composables/serverAuth';

const profile = ref<GetProfileResponse | undefined>(undefined);

const fetchWithCookies = (url: string, options?: { headers?: Record<string, string> }) => {
  return useFetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      ...useRequestHeaders(['cookie']),
    },
  });
};

type SessionResponse = {
  session: Session & { activeOrganizationId?: string; impersonatedBy?: string };
  user: User & { role?: string };
};

export const useAuthSession = async () => {
  try {
    const { data } = await useAuthClient().useSession(fetchWithCookies);
    const session = computed(() => data.value as unknown as SessionResponse | null);

    const fetchProfile = async () => {
      try {
        const data = await $fetch('/api/profile');
        profile.value = data;
      } catch (error) {
        posthogCatcher(error);
        profile.value = undefined;
      }
    };

    // The profile includes a membership role, so it requires an active organization.
    if (import.meta.client) {
      if (session.value?.session?.activeOrganizationId) {
        if (!profile.value) {
          await fetchProfile();
        }
      } else {
        profile.value = undefined;
      }
    }

    const name = computed(() => session.value?.user?.name || '');
    const email = computed(() => session.value?.user?.email || '');
    const id = computed(() => session.value?.user?.id || '');

    const avatarUrl = computed(() => session.value?.user?.image || undefined);
    const systemRole = computed(() => session.value?.user?.role || '');

    const organizationRole = computed(() => profile.value?.role || '');
    const organizationId = computed(() => session.value?.session?.activeOrganizationId || '');

    const isImpersonated = computed(() => session.value?.session?.impersonatedBy !== undefined);

    return {
      data: data as unknown as { value: SessionInfo },
      name,
      email,
      id,
      avatarUrl,
      systemRole,
      organizationRole,
      organizationId,
      isImpersonated,
    };
  } catch (error) {
    posthogCatcher(error);
    throw error;
  }
};
